import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useLeadsCampaignsQuery } from "Api/Queries/Lead/useLeadsCampaignsQuery";
import { getCampaignImageUrl } from "Api/Queries/Campaigns/useCampaignImageUrl";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { LinkDisplay } from "Components/Shared/LinkDisplay/LinkDisplay";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { CampaignImageType } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { ItemCardLink } from "Components/Shared/ItemCardLink/ItemCardLink";
import { useSearchParams } from "Navigation/useSearchParams";

const CAMPAIGNS_RESOURCES = Resources.Pages.Content.WhereToPay.Campaigns;
const PAGE_SIZE = 10;
const PAGE_NUMBER_PARAM = "pageNumber";

export const CampaignsSection = () => {
  const { t } = useResource();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get(PAGE_NUMBER_PARAM);
  const currentPage = Math.max(1, Number(pageParam) || 1);

  const {
    data: campaignsData,
    isLoading,
    error,
  } = useLeadsCampaignsQuery(PAGE_SIZE, currentPage);

  const totalCount = campaignsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = (_: unknown, page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page > 1) {
        next.set(PAGE_NUMBER_PARAM, String(page));
      } else {
        next.delete(PAGE_NUMBER_PARAM);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !campaignsData?.items) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <Alert severity="error">
            {t(Resources.Merchant.DataFailedToLoad)}
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      {campaignsData.items.map((campaign) => (
        <ItemCardLink
          key={campaign.code}
          title={campaign.name}
          subtitle={campaign.description}
          icon={
            <Avatar
              src={getCampaignImageUrl(campaign, CampaignImageType.Logo)}
              alt={campaign.name}
              variant="circular"
              sx={{ width: 48, height: 48, bgcolor: "transparent" }}
              slotProps={{ img: { style: { objectFit: "contain" } } }}
            >
              <VolunteerActivismIcon sx={{ color: "grey.600" }} />
            </Avatar>
          }
          to={campaign.url}
          target="_blank"
        >
          <Stack gap={2}>
            <Typography fontWeight={600}>
              {t(CAMPAIGNS_RESOURCES.ShareLinkText)}
            </Typography>
            <LinkDisplay>
              <Box
                component="code"
                flex={1}
                sx={{ lineBreak: "anywhere", fontSize: "0.75rem" }}
              >
                {campaign.url}
              </Box>
              <CopyToClipboard
                text={campaign.url}
                accessibilityLabel={t(CAMPAIGNS_RESOURCES.CopyLink)}
                successMessage={t(CAMPAIGNS_RESOURCES.LinkCopied)}
              />
            </LinkDisplay>
          </Stack>
        </ItemCardLink>
      ))}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <Stack gap={3} mt={2}>
      <Box mb={2}>
        <PrimaryPaper>
          <Stack gap={2}>
            <PaperTitle>{t(CAMPAIGNS_RESOURCES.Title)}</PaperTitle>
            <Typography>{t(CAMPAIGNS_RESOURCES.Description)}</Typography>
          </Stack>
        </PrimaryPaper>
      </Box>
      {children}
    </Stack>
  );
};
